/**
 * Flow-note helpers — host-anchored comment nodes.
 *
 * Notes are real canvas nodes (own id, box, renderer) but not work nodes:
 * they do not participate in progress, HUD, unlocks, or connection handles.
 * They occupy a real layout slot (same ELK / sync spacing as missions) and
 * stay in their host's chain via a host→note layout edge.
 */

import { HORIZONTAL_GAP, VERTICAL_GAP } from '../layout/constants';

export const NOTE_NODE_WIDTH = 290;
export const NOTE_NODE_HEIGHT = 88;
export const NOTE_HOST_GAP = 40;
export const NOTE_STACK_GAP = 16;

const DEFAULT_HOST_WIDTH = 290;
const DEFAULT_HOST_HEIGHT = 150;

export function isNoteNode(task) {
  return task?.nodeType === 'note';
}

export function isNonWorkNode(task) {
  const type = task?.nodeType;
  return type === 'milestone' || type === 'note';
}

export function isWorkNode(task) {
  return Boolean(task) && !isNonWorkNode(task);
}

/** @returns {{ hostTaskId: string } | null} */
export function getNoteAnchor(note) {
  const hostTaskId = note?.noteAnchor?.hostTaskId;
  return hostTaskId ? { hostTaskId } : null;
}

export function getNoteLayoutDims(nodeDimensions, taskId) {
  const measured = nodeDimensions?.[taskId];
  if (measured?.width && measured?.height) {
    return { width: measured.width, height: measured.height };
  }
  return { width: NOTE_NODE_WIDTH, height: NOTE_NODE_HEIGHT };
}

/** Notes grouped by host id, sorted by task order. */
export function buildNotesByHostMap(tasks) {
  const byHost = new Map();

  (tasks || [])
    .filter(isNoteNode)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((note) => {
      const hostTaskId = getNoteAnchor(note)?.hostTaskId;
      if (!hostTaskId) return;
      if (!byHost.has(hostTaskId)) byHost.set(hostTaskId, []);
      byHost.get(hostTaskId).push(note);
    });

  return byHost;
}

export function getNoteHostRelativePosition(hostPos, hostDims, stackIndex = 0, noteHeight = NOTE_NODE_HEIGHT) {
  const hostWidth = hostDims?.width ?? DEFAULT_HOST_WIDTH;
  return {
    x: hostPos.x + hostWidth + NOTE_HOST_GAP,
    y: hostPos.y + stackIndex * (noteHeight + NOTE_STACK_GAP),
  };
}

/**
 * Place attached notes to the right of their host after layout.
 * Mutates `positions`.
 */
export function applyNotePositionsAfterLayout(positions, tasks, nodeDimensions = {}) {
  const notesByHost = buildNotesByHostMap(tasks);

  (tasks || []).forEach((host) => {
    if (isNonWorkNode(host)) return;
    const hostPos = positions[host.id];
    if (!hostPos) return;

    const notes = notesByHost.get(host.id) || [];
    if (notes.length === 0) return;

    const hostDims = nodeDimensions[host.id] || {
      width: DEFAULT_HOST_WIDTH,
      height: DEFAULT_HOST_HEIGHT,
    };

    let y = hostPos.y;
    notes.forEach((note) => {
      const noteDims = getNoteLayoutDims(nodeDimensions, note.id);
      positions[note.id] = {
        x: hostPos.x + hostDims.width + NOTE_HOST_GAP,
        y,
      };
      y += noteDims.height + NOTE_STACK_GAP;
    });
  });
}

/**
 * True when a note still sits in the old "host.right + gap" slot — the same
 * cell ELK gives the next mission in the row. Saved coords like that must
 * not beat layout, or notes keep overlapping neighbors.
 */
export function isNoteParkedBesideHost(note, tasks, positions, nodeDimensions = {}) {
  if (!isNoteNode(note)) return false;
  const hostTaskId = getNoteAnchor(note)?.hostTaskId;
  if (!hostTaskId) return false;
  const notePos = positions?.[note.id];
  const hostPos = positions?.[hostTaskId];
  if (!notePos || !hostPos) return false;
  if (!Number.isFinite(notePos.x) || !Number.isFinite(notePos.y)) return false;
  if (!Number.isFinite(hostPos.x) || !Number.isFinite(hostPos.y)) return false;

  const hostDims = nodeDimensions[hostTaskId] || {
    width: DEFAULT_HOST_WIDTH,
    height: DEFAULT_HOST_HEIGHT,
  };
  const expectedX = hostPos.x + hostDims.width + NOTE_HOST_GAP;
  if (Math.abs(notePos.x - expectedX) > 2.5) return false;
  const relY = notePos.y - hostPos.y;
  return relY >= -2.5 && relY < 2000;
}

/** Suggested canvas position when attaching a note to a host (child row, not host-right). */
export function suggestNotePosition(hostTask, nodePositions = {}, nodeDimensions = {}, existingNotes = []) {
  const hostPos = nodePositions[hostTask.id] || { x: hostTask.x ?? 0, y: hostTask.y ?? 0 };
  const hostDims = nodeDimensions[hostTask.id] || {
    width: DEFAULT_HOST_WIDTH,
    height: DEFAULT_HOST_HEIGHT,
  };

  let x = hostPos.x;
  existingNotes.forEach((note) => {
    const dims = getNoteLayoutDims(nodeDimensions, note.id);
    x += dims.width + HORIZONTAL_GAP;
  });

  return {
    x,
    y: hostPos.y + hostDims.height + VERTICAL_GAP,
  };
}

/** When a host is deleted, its notes go with it. */
export function collectCascadedNoteIds(tasks, seedIds) {
  const ids = new Set(seedIds);
  (tasks || []).forEach((task) => {
    if (!isNoteNode(task)) return;
    const hostTaskId = getNoteAnchor(task)?.hostTaskId;
    if (hostTaskId && ids.has(hostTaskId)) {
      ids.add(task.id);
    }
  });
  return ids;
}
